import { useRef, useState } from 'react';
import { flushSync } from 'react-dom';
import { Icon } from '../../components/Icon';
import { useI18n } from '../../i18n/i18n-context';
import { rich } from '../../i18n/rich';
import { PromoPlayer, type PromoPlayerHandle } from '../promo/PromoPlayer';
import { FEATURE_PROMOS, clock, type FeatureId } from '../promo/promos';
import './feature-guides.css';

/** Independent feature demos, read and watched in a continuous page. */
export function FeatureGuides({ headingLevel = 2 }: { readonly headingLevel?: 1 | 2 }) {
  const { t } = useI18n();
  const Heading = headingLevel === 1 ? 'h1' : 'h2';
  const FilmHeading = headingLevel === 1 ? 'h2' : 'h3';
  const [selected, setSelected] = useState<FeatureId | null>(null);
  const player = useRef<PromoPlayerHandle>(null);

  return (
    <section
      className="feature-guides"
      id="feature-guides"
      aria-labelledby="feature-guides-heading"
    >
      <div className="feature-guides-intro">
        <Heading id="feature-guides-heading">{t.navigation.guide}</Heading>
        <p className="feature-guides-description">{t.home.featuresDescription}</p>
      </div>
      <div className="feature-guides-list">
        {FEATURE_PROMOS.map((film, index) => {
          const feature = t.home.features[film.id];
          const watchLabel = `${t.promo.play} · ${feature.label}`;
          return (
            <article
              className="feature-guide"
              key={film.id}
              aria-labelledby={`feature-guide-${film.id}`}
            >
              <div className="feature-guide-copy">
                <p className="feature-guide-kicker">
                  <span className="feature-guide-number" aria-hidden="true">
                    {String(index + 1).padStart(2, '0')}
                  </span>
                  {feature.label}
                </p>
                <FilmHeading id={`feature-guide-${film.id}`}>{feature.title}</FilmHeading>
                <p className="feature-guide-description">
                  {rich(feature.body, {
                    diff: <strong>{t.card.diff}</strong>,
                    why: <strong>{t.card.why}</strong>,
                    alts: <strong>{t.card.alts}</strong>,
                  })}
                </p>
              </div>
              <div className="feature-guide-media">
                {selected === film.id ? (
                  <PromoPlayer
                    ref={player}
                    src={film.src}
                    poster={film.poster}
                    captions={film.captions}
                    initialDuration={film.duration}
                    label={feature.title}
                    watchLabel={watchLabel}
                    preload="none"
                  />
                ) : (
                  <button
                    type="button"
                    className="feature-guide-preview"
                    aria-label={watchLabel}
                    onClick={() => {
                      // Start within the click gesture, including on browsers that restrict autoplay.
                      flushSync(() => {
                        setSelected(film.id);
                      });
                      player.current?.playFrom(0);
                      player.current?.video?.parentElement
                        ?.querySelector('button')
                        ?.focus({ preventScroll: true });
                    }}
                  >
                    <img
                      src={film.poster}
                      alt=""
                      width={1920}
                      height={1080}
                      loading={index === 0 ? 'eager' : 'lazy'}
                      decoding="async"
                    />
                    <span className="feature-guide-play" aria-hidden="true">
                      <Icon name="play" />
                    </span>
                    <span className="feature-guide-duration" dir="ltr" aria-hidden="true">
                      {clock(film.duration)}
                    </span>
                  </button>
                )}
              </div>
            </article>
          );
        })}
      </div>
    </section>
  );
}
